import services, {PricingFeesResponse} from "@/services";
import {useQuery} from "@tanstack/react-query";
import {paymentsV3On} from "./paymentsV3";

/**
 * The fee schedule THIS organiser is charged from, out of a /pricing-fees answer. Under payments v3
 * an Express account — or no account yet, which will be Express when connected — pays the all-in fee
 * (AllInFees, set from Master → Activation → Settings → Transaction fees): `payments_v3.schedule`.
 * A standard (pre-v3) account keeps PlatformFeeService's `schedule`, as ConnectedCharges charges it.
 *
 * Undefined until both the fees and, with v3 on, the account type have loaded, so a standard
 * organiser is never shown the other schedule first. Same query as billing's fee card.
 */
export function useFeeSchedule(fees: PricingFeesResponse | undefined, enabled = true): PricingFeesResponse['schedule'] | undefined {
    const v3 = paymentsV3On() && !!fees?.payments_v3?.enabled;
    const payout = useQuery({
        queryKey:  ['payout-account-status'],
        queryFn:   () => services.wallet.payoutAccountStatus(),
        staleTime: 60_000,
        enabled:   v3 && enabled,
    });
    if (!fees) return undefined;
    if (!v3) return fees.schedule;
    if (payout.isPending) return undefined;
    // An error reads as "not standard": v3 is what every organiser connecting today gets.
    return payout.data?.account_type === 'standard' ? fees.schedule : fees.payments_v3!.schedule;
}

/** The organiser's country row, else the default country's, else the US one (always priced). */
export function feeRowFor(schedule: PricingFeesResponse['schedule'] | undefined, country: string | null | undefined, defaultCountry = 'US') {
    if (!schedule) return undefined;
    return schedule[(country || defaultCountry).toUpperCase()] ?? schedule[defaultCountry] ?? schedule['US'];
}
